import React, { useState } from 'react'
import { DatePicker, Modal, Select, Spin } from 'antd'
import { Button, Table } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import styles from './UploadReview.module.scss'
import { DATE_INPUT_FORMATS, DUE_DATE_WIRE_FORMAT } from '../utils'
import { InvoiceTemplate } from '../../../api/dto/invoice-delivery.dto'
import { UploadEmailPreviewModal } from './UploadEmailPreviewModal'
import { getAllowedTemplatesForPeriod } from './invoiceTemplates'
import { buildReviewColumns, toTemplateOptions } from './uploadReviewColumns'
import { useUploadReview } from './useUploadReview'
import { ReviewFile, toDraftEmailRequest, UploadChoice } from './reviewRows'
import {
  FooterHint,
  ReviewBody,
  ReviewFooter,
  ReviewMeta,
  ReviewTableCard,
  ReviewToolbar,
  ToolbarField,
  ToolbarFieldEnd,
  ToolbarLabel,
} from './UploadReview.styles'

const REVIEW_PAGE_SIZE = 200
const OE1_DUE_DATE_ID = 'upload-review-oe1-due-date'
const SET_ALL_ID = 'upload-review-set-all'

export type { ReviewFile, UploadChoice }

interface UploadReviewModalProps {
  files: ReviewFile[]
  defaultTemplate: InvoiceTemplate
  billingPeriodStart: string
  billingPeriodLabel: string
  onCancel: () => void
  /** Only the files still listed, each with the values reviewed for it. */
  onConfirm: (choices: Record<string, UploadChoice>) => void
}

const UploadReviewModal: React.FC<UploadReviewModalProps> = ({
  files,
  defaultTemplate,
  billingPeriodStart,
  billingPeriodLabel,
  onCancel,
  onConfirm,
}) => {
  const review = useUploadReview(files, defaultTemplate, billingPeriodStart)
  const templateOptions = toTemplateOptions(
    getAllowedTemplatesForPeriod(billingPeriodStart)
  )
  const { rows, isReady } = review
  const [previewKey, setPreviewKey] = useState<string | null>(null)

  const columns = buildReviewColumns({
    clients: review.clients,
    templateOptions,
    onChooseClient: review.chooseClient,
    onTemplateChange: review.setTemplate,
    onEdit: review.editRow,
    onPreview: setPreviewKey,
    onRemove: review.removeFile,
  })

  const previewRow = rows.find((r) => r.key === previewKey)
  const fileCountLabel = `${rows.length} ${rows.length === 1 ? 'file' : 'files'}`
  const blockedReason = review.hasDuplicates
    ? 'Each company can have one file per email template.'
    : review.isOe1Incomplete
      ? 'Each OE - Initial Funding invoice needs both amounts and a due date.'
      : null

  return (
    <Modal
      open
      width={1060}
      className={styles.modal}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      title="Review upload"
    >
      <ReviewBody>
        <ReviewMeta>
          <strong>{billingPeriodLabel}</strong>
          {fileCountLabel} · values come from each sheet and can be retyped
        </ReviewMeta>

        <ReviewToolbar>
          {review.hasOe1 ? (
            <ToolbarField>
              <ToolbarLabel htmlFor={OE1_DUE_DATE_ID}>
                OE - Initial Funding due date
              </ToolbarLabel>
              <DatePicker
                id={OE1_DUE_DATE_ID}
                value={
                  isNil(review.oe1DueDate) ? null : dayjs(review.oe1DueDate)
                }
                onChange={(date) =>
                  review.setOe1DueDate(
                    isNil(date) ? null : date.format(DUE_DATE_WIRE_FORMAT)
                  )
                }
                format={DATE_INPUT_FORMATS}
                allowClear={false}
              />
            </ToolbarField>
          ) : null}
          <ToolbarFieldEnd>
            <ToolbarLabel htmlFor={SET_ALL_ID}>Set all to</ToolbarLabel>
            <Select
              id={SET_ALL_ID}
              placeholder="Email template"
              options={templateOptions}
              onChange={review.setAllTemplates}
              value={null}
              aria-label="Set every file to one email template"
              className={styles.templateSelect}
            />
          </ToolbarFieldEnd>
        </ReviewToolbar>

        <ReviewTableCard>
          <Spin spinning={!isReady}>
            <Table
              rowKey="key"
              size="small"
              dataSource={rows}
              columns={columns}
              pagination={{
                pageSize: REVIEW_PAGE_SIZE,
                size: 'small',
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total, [from, to]) =>
                  `${from}-${to} of ${total} ${total === 1 ? 'file' : 'files'}`,
              }}
              scroll={{ y: 460 }}
              locale={{
                emptyText:
                  'Every file was removed. Cancel to pick files again.',
              }}
            />
          </Spin>
        </ReviewTableCard>

        <ReviewFooter>
          {isReady && !isNil(blockedReason) ? (
            <FooterHint role="status">{blockedReason}</FooterHint>
          ) : null}
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            disabled={!isReady || rows.length === 0 || !isNil(blockedReason)}
            onClick={() => onConfirm(review.choices)}
          >
            Upload {fileCountLabel}
          </Button>
        </ReviewFooter>
      </ReviewBody>

      {isNil(previewRow) ? null : (
        <UploadEmailPreviewModal
          fileName={previewRow.file.name}
          companyName={
            previewRow.client?.name ??
            previewRow.parsed?.companyName ??
            'your company'
          }
          request={toDraftEmailRequest(previewRow, billingPeriodStart)}
          onClose={() => setPreviewKey(null)}
        />
      )}
    </Modal>
  )
}

export { UploadReviewModal }
